import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as VisuallyHiddenPrimitive from "@radix-ui/react-visually-hidden"
import { IconX } from "@tabler/icons-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * Fallback title for accessibility when no DialogTitle is provided.
   * If you need to hide the title visually, wrap your DialogTitle with VisuallyHidden.
   */
  fallbackTitle?: string
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, fallbackTitle = "Dialog", ...props }, ref) => {
  const hasDialogTitle = React.useMemo(() => {
    const check = (node: React.ReactNode): boolean => {
      if (node === null || node === undefined || typeof node === "boolean") return false
      if (Array.isArray(node)) return node.some(check)
      if (React.isValidElement(node)) {
        const elementType = node.type as any
        const displayName = elementType?.displayName || elementType?.name
        if (
          elementType === DialogPrimitive.Title ||
          displayName === DialogPrimitive.Title.displayName ||
          elementType === DialogTitle
        ) {
          return true
        }
        if (node.props?.children) {
          return check(node.props.children)
        }
      }
      return false
    }
    return check(children)
  }, [children])

  const hasDialogDescription = React.useMemo(() => {
    const check = (node: React.ReactNode): boolean => {
      if (node === null || node === undefined || typeof node === "boolean") return false
      if (Array.isArray(node)) return node.some(check)
      if (React.isValidElement(node)) {
        const elementType = node.type as any
        const displayName = elementType?.displayName || elementType?.name
        if (
          elementType === DialogPrimitive.Description ||
          displayName === DialogPrimitive.Description.displayName ||
          elementType === DialogDescription
        ) {
          return true
        }
        if (node.props?.children) {
          return check(node.props.children)
        }
      }
      return false
    }
    return check(children)
  }, [children])

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}
      >
        {/* Fallback hidden title to satisfy Radix accessibility requirement when missing */}
        {!hasDialogTitle && !props["aria-label"] && (
          <VisuallyHiddenPrimitive.Root>
            <DialogPrimitive.Title>{fallbackTitle}</DialogPrimitive.Title>
          </VisuallyHiddenPrimitive.Root>
        )}
        {/* Fallback hidden description to satisfy Radix accessibility requirement when missing */}
        {!hasDialogDescription && !props["aria-describedby"] && (
          <VisuallyHiddenPrimitive.Root>
            <DialogPrimitive.Description>Dialog content</DialogPrimitive.Description>
          </VisuallyHiddenPrimitive.Root>
        )}
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <IconX className="h-4 w-4" stroke={1.5} />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

const VisuallyHidden = VisuallyHiddenPrimitive.Root

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  VisuallyHidden,
}
